import type { ConversationDTO } from '@next-space/fe-api-idl';
import type { FC } from 'react';
import { memo, useCallback, useRef } from 'react';
import { I18nextProvider } from 'react-i18next';
import { API } from '../../api';
import { ScrollLoadList } from '../../components/scroll-load-list';
import { cx } from '../../cx';
import { Icon } from '../../icon-svg';
import { useOpenModal } from '../../next-modal';
import { useChatTranslation } from '../hooks/use-i18n';
import i18n from '../i18n/i18n';

interface ChatHistoryProps {
  id?: string;
  onBack: () => void;
  onNew: () => void;
  onClick: (id: string) => void;
  spaceId: string;
}

/**
 * 聊天历史记录组件
 *
 * @example
 * ```tsx
 * const ChatHistoryWrapper = () => {
 *   // 返回上一页
 *   const onBack = () => {
 *     navigateToChat(id);
 *   };
 *
 *   // 新建聊天
 *   const onNew = () => {
 *     navigateToChat('');
 *   };
 *
 *   // 点击历史记录
 *   const onClick = (id: string) => {
 *     navigateToChat(id);
 *   };
 *
 *   return (
 *     <ChatHistory
 *       id={currentChatId}
 *       onBack={onBack}
 *       onNew={onNew}
 *       onClick={onClick}
 *     />
 *   );
 */
const ChatHistoryWithI18n: FC<ChatHistoryProps> = (props) => {
  const { id, onBack, onNew, onClick, spaceId } = props;
  const { t } = useChatTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const getApi = async (params: { pageIndex: number; pageSize: number }) => {
    const res = await API.ai.getQaHistory(spaceId, params.pageIndex, params.pageSize);
    return { list: res.list, more: res.more };
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex justify-between items-center px-3 py-2 text-grey3">
        <Icon
          name="IcFold"
          size="middle"
          boxSize="large"
          className="animate-hover"
          onClick={onBack}
        />
        {/* <Icon
          name="IcAdd"
          size="middle"
          boxSize="large"
          className="animate-hover"
          onClick={onNew}
        /> */}
      </div>
      {/* <div className="px-4 py-1.5">
        <Input className="py-0.5" value={keyword} onChange={setKeyword} />
      </div> */}
      <div className="flex-1 overflow-y-auto pt-2 text-t2" ref={containerRef}>
        <ScrollLoadList
          container={containerRef}
          getApi={getApi}
          placeholder={t('chatHistoryEmpty')}
          // filterFn={filterFn}
          // keyword={keyword}
          renderItem={(item: ConversationDTO, _, opt) => (
            <ChatHistoryItem
              spaceId={spaceId}
              onClick={() => onClick(item.id)}
              active={id === item.id}
              key={item.id}
              item={item}
              opt={opt}
            />
          )}
        />
      </div>
    </div>
  );
};

interface ChatHistoryItemProps {
  item: ConversationDTO;
  active: boolean;
  onClick: () => void;
  spaceId: string;
  opt: {
    delete: (id: string) => void;
  };
}

const ChatHistoryItem: FC<ChatHistoryItemProps> = memo((props) => {
  const { item, active, onClick, opt } = props;
  const openModal = useOpenModal();
  const { t } = useChatTranslation();

  const deleteChatHistory = useCallback(() => {
    openModal.warning({
      title: t('chatHistoryDeleteTitle'),
      content: t('chatHistoryDeleteContent'),
      confirm: () => {
        opt.delete(item.id);
        void API.ai.deleteConversationById(item.id);
      },
    });
  }, [item.id, openModal, opt, t]);

  return (
    <div
      className={cx(
        'flex justify-between items-center px-4 py-1.5 animate-hover-black3 rounded-none mb-2 space-x-2 group',
        active && 'bg-black_003'
      )}
      onClick={onClick}
    >
      <span className="truncate flex-1">{item.question}</span>
      <Icon
        name="IcTrash"
        size="xsmall"
        boxSize="middle"
        className="text-grey3 animate-hover hidden group-hover:flex"
        onClick={(e) => {
          e.stopPropagation();
          void deleteChatHistory();
        }}
      />
    </div>
  );
});

export const ChatHistory: FC<ChatHistoryProps> = (props) => {
  return (
    <I18nextProvider i18n={i18n}>
      <ChatHistoryWithI18n {...props} />
    </I18nextProvider>
  );
};
